import { useEffect, useState } from 'react';
import styles from '../PdfRenderer.module.scss';

/**
 * Действия над замечанием, для которых обязателен непустой комментарий.
 * Список живёт здесь, а не в NoteActions: диалог теперь общий, его открывают
 * и кнопки в раскрытой панели, и кнопка «Выполнить» на самой карточке.
 */
export const COMMENT_REQUIRED_ACTIONS = ['cancel', 'return', 'confirm-not-completed', 'cancel-by-initiator', 'complete', 'complete-gip'] as const;

export type PendingActionType = typeof COMMENT_REQUIRED_ACTIONS[number];

/** Заголовок окна */
const TITLES: Record<PendingActionType, string> = {
    'cancel': 'Отмена замечания',
    'cancel-by-initiator': 'Отмена замечания',
    'return': 'Возврат эксперту',
    'confirm-not-completed': 'Замечание не выполнено',
    'complete': 'Выполнение замечания',
    'complete-gip': 'Выполнение замечания',
};

/** Подпись над полем ввода */
const FIELD_LABELS: Record<PendingActionType, string> = {
    'cancel': 'Причина отмены',
    'cancel-by-initiator': 'Причина отмены',
    'return': 'Комментарий для эксперта',
    'confirm-not-completed': 'Причина невыполнения',
    'complete': 'Комментарий к выполнению',
    'complete-gip': 'Комментарий к выполнению',
};

/** Подпись подтверждающей кнопки */
const CONFIRM_LABELS: Record<PendingActionType, string> = {
    'cancel': 'Отменить замечание',
    'cancel-by-initiator': 'Отменить замечание',
    'return': 'Вернуть',
    'confirm-not-completed': 'Подтвердить',
    'complete': 'Выполнить',
    'complete-gip': 'Выполнено',
};

const PLACEHOLDERS: Partial<Record<PendingActionType, string>> = {
    'complete': 'Опишите, что и на каком листе исправлено',
    'complete-gip': 'Опишите, что и на каком листе исправлено',
};

interface CommentActionModalProps {
    type: PendingActionType;
    /** Номер замечания в заголовке. 0/undefined — не показывать. */
    noteId?: number;
    onConfirm: (comment: string) => void;
    onCancel: () => void;
}

export function CommentActionModal({ type, noteId, onConfirm, onCancel }: CommentActionModalProps) {
    const [comment, setComment] = useState('');

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel]);

    const trimmed = comment.trim();

    const confirm = () => {
        // Комментарий обязателен для всех этих действий — без guard'а можно было
        // отправить пустую причину отмены или невыполнения.
        if (!trimmed) return;
        onConfirm(trimmed);
    };

    return (
        <div
            className={styles.commentModalBackdrop}
            onClick={onCancel}
            onMouseDown={e => e.stopPropagation()}
        >
            <div className={styles.commentModal} onClick={e => e.stopPropagation()}>
                <div className={styles.commentModalHeader}>
                    <span className={styles.commentModalTitle}>{TITLES[type]}</span>
                    {!!noteId && noteId > 0 && (
                        <span className={styles.commentModalNote}>{noteId}</span>
                    )}
                    <button
                        type="button"
                        className={styles.commentModalClose}
                        onClick={onCancel}
                        title="Закрыть"
                        aria-label="Закрыть"
                    >
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>

                <div className={styles.commentModalBody}>
                    <div className={styles.commentModalLabel}>{FIELD_LABELS[type]}</div>
                    <textarea
                        className={styles.commentModalTextarea}
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder={PLACEHOLDERS[type] ?? 'Введите комментарий…'}
                        autoFocus
                    />
                </div>

                <div className={styles.commentModalFooter}>
                    <button
                        type="button"
                        className={styles.commentModalConfirm}
                        disabled={!trimmed}
                        onClick={confirm}
                    >
                        <i className="fa-solid fa-check" />
                        {CONFIRM_LABELS[type]}
                    </button>
                    <button
                        type="button"
                        className={styles.commentModalCancel}
                        onClick={onCancel}
                    >
                        Отмена
                    </button>
                </div>
            </div>
        </div>
    );
}
